import type { MessageDto } from '../api/types';

/**
 * Разбор сообщения из WebSocket-конверта в тот же DTO, что отдаёт REST.
 *
 * Отдельный модуль, а не приватная функция провайдера: провайдер тянет за собой
 * нативные модули (звук, пуши), и тест на разбор из-за них не поднимался.
 */
function pickField(o: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(o, k) && o[k] != null) {
      return o[k];
    }
  }
  return undefined;
}

function stringifyInstantish(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return new Date(v < 1e12 ? v * 1000 : v).toISOString();
  }
  if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'number') {
    const sec = Number(v[0]);
    if (!Number.isNaN(sec)) return new Date(sec * 1000).toISOString();
  }
  return String(v);
}

/** Поля отправителя здесь уже однажды терялись — см. стори 5.2. */
export function normalizeMessage(raw: unknown): MessageDto {
  if (!raw || typeof raw !== 'object') {
    throw new Error('invalid message');
  }
  const o = raw as Record<string, unknown>;
  const id = pickField(o, 'id', 'messageId');
  const senderId = pickField(o, 'senderId', 'sender_id');
  const senderDisplayName = pickField(o, 'senderDisplayName', 'sender_display_name');
  const ciphertext = pickField(o, 'ciphertext', 'cipherText', 'body');
  const createdAt = pickField(o, 'createdAt', 'created_at');
  const replyToMessageId = pickField(o, 'replyToMessageId', 'reply_to_message_id');
  const forwarded = pickField(o, 'forwarded', 'isForwarded', 'is_forwarded');
  const forwardedFromMessageId = pickField(o, 'forwardedFromMessageId', 'forwarded_from_message_id');
  const reactions = pickField(o, 'reactions');
  const myReactions = pickField(o, 'myReactions');
  const readByAnyRecipient = pickField(o, 'readByAnyRecipient');
  /**
   * Поля отправителя раньше здесь терялись, и сообщение, доехавшее по WebSocket,
   * рисовалось без аватара — в отличие от того же сообщения, подтянутого через
   * REST при следующем открытии чата. Отсюда «аватары и подписи участников
   * периодически пропадают» (стори 5.2).
   */
  const senderHasAvatar = pickField(o, 'senderHasAvatar', 'sender_has_avatar');
  const senderAvatarRevision = pickField(o, 'senderAvatarRevision', 'sender_avatar_revision');
  const senderAvatarShape = pickField(o, 'senderAvatarShape', 'sender_avatar_shape');
  const commentPostId = pickField(o, 'commentPostId', 'comment_post_id');
  const commentCount = pickField(o, 'commentCount', 'comment_count');
  /**
   * Метка правки. Без неё правка, приехавшая по сокету, показывалась бы у собеседника новым
   * текстом без единого признака подмены — ровно то, ради чего метка и заведена; «изменено»
   * появлялось бы только после следующей загрузки чата через REST. Тот же класс потери, что и
   * с полями отправителя выше.
   */
  const editedAt = pickField(o, 'editedAt', 'edited_at');
  if (id == null || senderId == null || ciphertext == null) {
    throw new Error('invalid message fields');
  }
  return {
    id: String(id),
    senderId: String(senderId),
    senderDisplayName: senderDisplayName != null ? String(senderDisplayName) : null,
    ciphertext: String(ciphertext),
    createdAt: stringifyInstantish(createdAt),
    replyToMessageId: replyToMessageId != null ? String(replyToMessageId) : null,
    forwarded: typeof forwarded === 'boolean' ? forwarded : null,
    forwardedFromMessageId: forwardedFromMessageId != null ? String(forwardedFromMessageId) : null,
    reactions: reactions && typeof reactions === 'object' ? (reactions as Record<string, number>) : null,
    myReactions: Array.isArray(myReactions) ? myReactions.map(String) : null,
    readByAnyRecipient: typeof readByAnyRecipient === 'boolean' ? readByAnyRecipient : null,
    ...(senderHasAvatar != null ? { senderHasAvatar: senderHasAvatar === true || senderHasAvatar === 'true' } : {}),
    ...(senderAvatarRevision != null && Number.isFinite(Number(senderAvatarRevision))
      ? { senderAvatarRevision: Number(senderAvatarRevision) }
      : {}),
    ...(senderAvatarShape != null ? { senderAvatarShape: String(senderAvatarShape) } : {}),
    ...(editedAt != null ? { editedAt: stringifyInstantish(editedAt) } : {}),
    ...(commentPostId != null ? { commentPostId: String(commentPostId) } : {}),
    ...(commentCount != null && Number.isFinite(Number(commentCount))
      ? { commentCount: Number(commentCount) }
      : {}),
  };
}
